/**
 * Agentic Payment Offers - Utility Functions
 */

import { colors, gradients } from "./styles";

// ─── Date ───────────────────────────────────────────────────────────────────

export const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
};

// ─── Amount ─────────────────────────────────────────────────────────────────

export const formatAmount = (value, decimals = 2) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return num.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

// ─── Text ────────────────────────────────────────────────────────────────────

export const truncateText = (text, maxLen = 24) => {
  if (!text) return "-";
  return text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
};

// ─── Status ──────────────────────────────────────────────────────────────────

/**
 * Derive a human-readable status label from the offer's computed flags.
 * Mirrors the logic described in the API documentation.
 */
export const getOfferStatus = (offer) => {
  if (offer.deletedAt) return "Deleted";
  if (offer.delistedAt) return "Delisted";
  if (offer.isSettled) return "Settled";
  if (offer.isExpired) return "Expired";
  if (!offer.isActive) return "Disabled";
  if (offer.isCutoffPassed) return "Cutoff Passed";
  if (offer.canEngage) return "Live";
  return "Unknown";
};

export const getStatusBadgeStyle = (status) => {
  const base = {
    padding: "3px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: 600,
    display: "inline-block",
    whiteSpace: "nowrap",
  };

  switch (status) {
    case "Live":
      return { ...base, background: colors.success, color: colors.white };
    case "Expired":
      return { ...base, background: colors.danger, color: colors.white };
    case "Settled":
      return { ...base, background: gradients.warning, color: colors.gray700 };
    case "Delisted":
      return { ...base, background: "#e67e22", color: colors.white };
    case "Deleted":
      return { ...base, background: "#7f1d1d", color: colors.white };
    case "Disabled":
      return { ...base, background: colors.gray300, color: colors.gray700 };
    case "Cutoff Passed":
      return { ...base, background: colors.info, color: colors.white };
    default:
      return { ...base, background: colors.gray400, color: colors.gray700 };
  }
};

export const getOfferTypeBadgeStyle = (offerType) => {
  const base = {
    padding: "3px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: 600,
    display: "inline-block",
    textTransform: "capitalize",
  };

  return offerType === "long"
    ? { ...base, background: gradients.success, color: colors.white }
    : { ...base, background: gradients.danger, color: colors.white };
};

// ─── Pagination Range ────────────────────────────────────────────────────────

export const calculatePaginationRange = (
  currentPage,
  totalPages,
  delta = 2,
) => {
  const range = [];
  const left = Math.max(2, currentPage - delta);
  const right = Math.min(totalPages - 1, currentPage + delta);

  range.push(1);
  if (left > 2) range.push("...");
  for (let i = left; i <= right; i++) range.push(i);
  if (right < totalPages - 1) range.push("...");
  if (totalPages > 1) range.push(totalPages);

  return range.filter((v, i, a) => a.indexOf(v) === i);
};
